import { Form, Formik } from "formik";
import { FC, useState } from "react";
import PhoneFormatter from "react-headless-phone-input/lazy";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, Input, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";
import ChevronLeftIcon from "@/svgs/angle_left_b.svg";
import { PHONE_COUNTRIES } from "@/utils";

import styles from "./phone.module.scss";
import { PhoneTabFormScheme } from "./phone.types";

const validationScheme = object({
  phoneNumber: string().min(1, "Field is required"),
});

const PhoneTab: FC = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const phoneNumber = useOnboardingStore((state) => state.userInfo.phoneNumber);
  const { setCurrentStep, setInfo, addPassedStep } = useOnboardingStore((state) => state.actions);

  const [countryOption, setCountryOption] = useState(PHONE_COUNTRIES[0]);

  const onSubmitHandler = (data: PhoneTabFormScheme) => {
    setInfo(data);
    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };

  return (
    <main className={styles.phoneTab}>
      <Typography semi variant="title-2">
        Enter your phone number
      </Typography>

      <Formik<PhoneTabFormScheme>
        initialValues={{ phoneNumber: phoneNumber ?? "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, isValid, setFieldValue, validateField }) => (
          <PhoneFormatter
            defaultCountry={"US"}
            value={values.phoneNumber}
            onChange={(v) => setFieldValue("phoneNumber", v)}
          >
            {({ country, impossible, onBlur, onInputChange, inputValue }) => (
              <Form className={styles.form}>
                <Input
                  type="tel"
                  placeholder="Mobile Number"
                  variant={impossible || errors.phoneNumber ? "error" : undefined}
                  message={
                    impossible || errors.phoneNumber
                      ? impossible
                        ? "Invalid phone number"
                        : errors.phoneNumber
                      : undefined
                  }
                  value={inputValue}
                  startContent={
                    <div className={styles.leftContent}>
                      <ChevronLeftIcon className={styles.icon} />
                      <Typography variant="subheadline">{country || countryOption?.id}</Typography>

                      <select
                        className={styles.select}
                        value={countryOption?.id}
                        onChange={({ target: { value } }) => {
                          const option = PHONE_COUNTRIES.find(({ id }) => id === value);

                          if (option) {
                            setCountryOption(option);
                            onInputChange(option?.startNumber);
                          }
                        }}
                      >
                        {PHONE_COUNTRIES.map(({ id, fullName }) => (
                          <option key={id} value={id}>
                            {fullName}
                          </option>
                        ))}
                      </select>
                    </div>
                  }
                  onBlur={() => {
                    onBlur();
                    validateField("phoneNumber");
                  }}
                  onChange={async ({ target: { value } }) => {
                    onInputChange(value || countryOption?.startNumber || "+1");
                    setCountryOption(PHONE_COUNTRIES.find(({ id }) => id === country));

                    await setFieldValue("phoneNumber", value);
                    await validateField("phoneNumber");
                  }}
                />

                <Button fullWidth size="big" type="submit" disabled={!isValid || !values.phoneNumber || !!impossible}>
                  Continue
                </Button>
              </Form>
            )}
          </PhoneFormatter>
        )}
      </Formik>
    </main>
  );
};

export { PhoneTab };
